/**
 * Repeatable instance editor chrome — breadcrumb + prev/next (#1154).
 * Mirrors {@link RackEditChrome} for UPS, cooling, liquid cooling, facility power.
 */

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';

import { colors, spacing } from '../../theme';
import { ChevronLeft, ChevronRight } from '../icons';
import { instanceIdsEqual } from './repeatableSectionUtils';

import type { RepeatableInstance } from './types';

export interface RepeatableInstanceEditChromeProps<
  T extends RepeatableInstance,
> {
  isNew: boolean;
  instancesForNavigation: T[];
  currentInstanceId?: number;
  /** Live or saved instance name — falls back when empty. */
  instanceTitle?: string;
  backToListLabel: string;
  newInstanceFallbackTitle?: string;
  entityPrefix?: string;
  onBackToList: () => void;
  onNavigateToInstance: (instance: T) => void;
  onConfirmDiscard?: () => boolean | Promise<boolean>;
  testID?: string;
  showBreadcrumb?: boolean;
}

export function RepeatableInstanceEditChrome<T extends RepeatableInstance>({
  isNew,
  instancesForNavigation,
  currentInstanceId,
  instanceTitle,
  backToListLabel,
  newInstanceFallbackTitle = 'New record',
  entityPrefix = 'Item',
  onBackToList,
  onNavigateToInstance,
  onConfirmDiscard,
  testID = 'repeatable-instance-edit-chrome',
  showBreadcrumb = true,
}: RepeatableInstanceEditChromeProps<T>) {
  const total = instancesForNavigation.length;
  const currentIndex =
    !isNew && currentInstanceId != null
      ? instancesForNavigation.findIndex(item =>
          instanceIdsEqual(item.id, currentInstanceId),
        )
      : -1;
  const canPaginate = !isNew && total >= 1 && currentIndex >= 0;
  const positionLabel =
    canPaginate && total > 1 ? `${currentIndex + 1} of ${total}` : null;
  // A NEW record titles the chrome too (mirrors RackEditChrome: live name,
  // else the new-record fallback) — #997 Story 6.3 R5 / GATE-5.
  const chromeTitle =
    instanceTitle?.trim() || (isNew ? newInstanceFallbackTitle : null);
  const paginationText =
    canPaginate && chromeTitle && positionLabel
      ? `${chromeTitle} · ${positionLabel}`
      : canPaginate && chromeTitle
      ? chromeTitle
      : canPaginate
      ? `${entityPrefix} ${currentIndex + 1} of ${total}`
      : isNew && total > 0
      ? chromeTitle ?? newInstanceFallbackTitle
      : null;

  const tryNavigate = async (nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= total) {
      return;
    }
    if (onConfirmDiscard) {
      const mayLeave = await onConfirmDiscard();
      if (!mayLeave) {
        return;
      }
    }
    const next = instancesForNavigation[nextIndex];
    if (next) {
      onNavigateToInstance(next);
    }
  };

  return (
    <View style={styles.wrap} testID={testID}>
      <View style={styles.topBar}>
        {showBreadcrumb ? (
          <TouchableOpacity
            testID={`${testID}-back-to-list`}
            style={styles.breadcrumb}
            onPress={onBackToList}
            accessibilityRole="button"
            accessibilityLabel={`Back to ${backToListLabel}`}
          >
            <ChevronLeft size={18} color={colors.primary} />
            <Text
              style={styles.breadcrumbText}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {backToListLabel}
            </Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.breadcrumbPlaceholder} />
        )}

        {canPaginate ? (
          <View style={styles.pagination}>
            <TouchableOpacity
              testID={`${testID}-prev`}
              style={[
                styles.pageButton,
                currentIndex <= 0 && styles.pageButtonDisabled,
              ]}
              onPress={() => void tryNavigate(currentIndex - 1)}
              disabled={currentIndex <= 0}
              accessibilityLabel="Previous item"
              accessibilityRole="button"
            >
              <ChevronLeft size={16} color={colors.textSecondary} />
            </TouchableOpacity>
            <Text
              style={styles.paginationText}
              numberOfLines={1}
              ellipsizeMode="tail"
              testID={`${testID}-position`}
            >
              {paginationText}
            </Text>
            <TouchableOpacity
              testID={`${testID}-next`}
              style={[
                styles.pageButton,
                currentIndex >= total - 1 && styles.pageButtonDisabled,
              ]}
              onPress={() => void tryNavigate(currentIndex + 1)}
              disabled={currentIndex >= total - 1}
              accessibilityLabel="Next item"
              accessibilityRole="button"
            >
              <ChevronRight size={16} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        ) : paginationText ? (
          <Text
            style={[styles.paginationText, styles.titleOnly]}
            numberOfLines={1}
            ellipsizeMode="tail"
            testID={`${testID}-title`}
          >
            {paginationText}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexShrink: 0,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
    minWidth: 0,
    ...Platform.select({
      web: { cursor: 'pointer' as unknown as undefined },
    }),
  },
  breadcrumbText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.primary,
  },
  breadcrumbPlaceholder: {
    flex: 1,
  },
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 1,
    minWidth: 0,
  },
  pageButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.borderCard,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
  },
  pageButtonDisabled: {
    opacity: 0.4,
  },
  paginationText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    flexShrink: 1,
    minWidth: 0,
    textAlign: 'center',
  },
  titleOnly: {
    flex: 1,
    textAlign: 'left',
  },
});
